import type React from 'react';

import { getLunaSampleUrl } from '@vlabs/api/axios/lp5-client';
import { useGetEvent } from '@vlabs/api/luna-api/client';
import type { EventMatchingDescriptorDescriptorType } from '@vlabs/api/luna-api/model/EventMatchingDescriptorDescriptorType';
import { DetectionIcon, ArrowInCircleRightIcon } from '@vlabs/icons';
import HandlerLink from '@vlabs/shared/components/handler-link/HandlerLink';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { EventStreamLink } from '@vlabs/shared/components/stream-link/EventStreamLink';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { routes } from '@vlabs/shared/navigation/routes';
import { formatDateTime, DATE_FORMATS } from '@vlabs/shared/utils/date';
import { Tag } from '@vlabs/uikit';
import { Link } from '@vlabs/uikit/components/controls/link/Link';
import { generatePath } from 'react-router-dom';

import { EventCardSample } from '../EventCardSample';
import { useEventCardTranslation } from '../i18n';
import st from './shared.module.sass';

interface EventMatchCardProps {
  eventId: string;
  matcherType?: EventMatchingDescriptorDescriptorType;
  similarity?: number;
}

function EventMatchCard({
  eventId,
  matcherType = 'face',
  similarity = undefined,
}: EventMatchCardProps): React.JSX.Element | null {
  const { t } = useEventCardTranslation();
  const { data: event } = useGetEvent(eventId);

  const faceSampleId = event?.face_detections?.find(({ sample_id }) => sample_id !== null)?.sample_id;
  const bodySampleId = event?.body_detections?.find(({ sample_id }) => sample_id !== null)?.sample_id;
  const sampleFaceSrc = getLunaSampleUrl('faces', faceSampleId);
  const sampleBodySrc = getLunaSampleUrl('bodies', bodySampleId);

  if (!event) return null;

  return (
    <div className={st.Card}>
      <div className={st.PrimarySection}>
        <EventCardSample
          matcherType={matcherType}
          sampleBodySrc={sampleBodySrc}
          sampleFaceSrc={sampleFaceSrc}
        />
        <div className={st.Details}>
          <Tag
            kind="neutral"
            size="large"
            value={t('match_tag')}
          />
          {similarity != null && (
            <SimilarityTag
              icon={<DetectionIcon />}
              kind="primary"
              size="large"
              title={t('similarity_event')}
              value={similarity}
            />
          )}
          <div className={st.Date}>{formatDateTime(event?.create_time, DATE_FORMATS.withYear)}</div>
        </div>
      </div>
      <div className={st.SecondarySection}>
        <div className={st.RowItem}>
          <div className={st.RowTitle}>{t('source')}</div>
          <TruncatedText
            hasTruncation
            tooltipContent={event.stream_id ?? ''}
          >
            <EventStreamLink
              kind="secondary"
              streamId={event.stream_id}
            />
          </TruncatedText>
        </div>
        <div className={st.RowItem}>
          <div className={st.RowTitle}>{t('handler')}</div>
          <TruncatedText
            hasTruncation
            tooltipContent={event.handler_id ?? ''}
          >
            <HandlerLink
              handlerId={event?.handler_id}
              kind="secondary"
            />
          </TruncatedText>
        </div>
      </div>
      <Link
        className={st.Link}
        icon={<ArrowInCircleRightIcon />}
        size="medium"
        target="_blank"
        // @ts-expect-error lack routes types
        to={generatePath(routes?.events?.eventDetails, { eventId })}
      >
        {t('event_link')}
      </Link>
    </div>
  );
}

export { EventMatchCard };
